import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaFileAlt, FaGlobe } from 'react-icons/fa';

const Resume = ({ darkMode }) => {
    const { t, i18n } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

    // Seleccionar el CV según el idioma elegido
    const pdfFile = selectedLanguage === 'es' 
        ? `${process.env.PUBLIC_URL}/Starlin_Frias_CV_ES.pdf`
        : `${process.env.PUBLIC_URL}/Starlin_Frias_CV_EN.pdf`;
    
    const downloadFileName = selectedLanguage === 'es' 
        ? 'Starlin_Frias_CV_ES.pdf'
        : 'Starlin_Frias_CV_EN.pdf';
    
    const qualifications = t('resume.qualificationsList', { returnObjects: true });

    // Estilos condicionales
    const downloadBtnStyle = darkMode
        ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
        : 'bg-cyan-500 hover:bg-cyan-600 text-white';

    const viewBtnStyle = darkMode
        ? 'bg-gray-700 hover:bg-gray-600 text-white'
        : 'bg-gray-200 hover:bg-gray-300 text-gray-800';

    const overlayBtnStyle = darkMode
        ? 'bg-gray-800 text-white'
        : 'bg-white text-gray-800';

    const textColor = darkMode ? 'text-gray-300' : 'text-gray-600';
    const headingColor = darkMode ? 'text-gray-200' : 'text-gray-800';
    const sectionBg = darkMode ? 'bg-gray-800' : 'bg-white';
    const titleColor = darkMode ? 'text-cyan-400' : 'text-cyan-600';

    if (!pdfFile) return null;

    return (
        <section id="resume" className={`py-12 ${sectionBg} rounded-xl p-8 shadow-lg`}>
            <h2 className={`text-3xl font-bold mb-8 text-center ${titleColor}`}>
                {t('resume.title')}
            </h2>

            {/* Selector de idioma del CV */}
            <div className="flex justify-center mb-6">
                <div className={`inline-flex rounded-lg border ${
                    darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-100'
                } p-1`}>
                    <button
                        onClick={() => setSelectedLanguage('es')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                            selectedLanguage === 'es'
                                ? darkMode
                                    ? 'bg-cyan-600 text-white'
                                    : 'bg-cyan-500 text-white'
                                : darkMode
                                    ? 'text-gray-300 hover:text-white'
                                    : 'text-gray-700 hover:text-gray-900'
                        }`}
                    >
                        <FaGlobe />
                        <span>CV Español</span>
                    </button>
                    <button
                        onClick={() => setSelectedLanguage('en')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                            selectedLanguage === 'en'
                                ? darkMode
                                    ? 'bg-cyan-600 text-white'
                                    : 'bg-cyan-500 text-white'
                                : darkMode
                                    ? 'text-gray-300 hover:text-white'
                                    : 'text-gray-700 hover:text-gray-900'
                        }`}
                    >
                        <FaGlobe />
                        <span>CV English</span>
                    </button>
                </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8">
                <a
                    href={pdfFile}
                    download={downloadFileName}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium ${downloadBtnStyle} transition-colors`}
                >
                    <FaDownload className="mr-2" />
                    {t('resume.download')}
                </a>

                <a
                    href={pdfFile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center px-4 py-2 rounded-lg font-medium ${viewBtnStyle} transition-colors`}
                >
                    <FaFileAlt className="mr-2" />
                    {t('resume.view')}
                </a>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex justify-center"
            >
                <div className={`relative w-full max-w-2xl h-[70vh] md:h-[80vh] rounded-lg overflow-hidden shadow-xl ${
                    darkMode ? 'shadow-black/30' : 'shadow-gray-400/30'
                }`}>
                    <iframe
                        src={`${pdfFile}#toolbar=0&navpanes=0`}
                        title="Currículum de Starlin Frías"
                        className="w-full h-full border-0"
                        loading="lazy"
                    />

                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all flex items-center justify-center">
                        <div className="opacity-0 hover:opacity-100 transition-all">
                            <a
                                href={pdfFile}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center px-6 py-3 rounded-lg font-medium bg-opacity-90 ${overlayBtnStyle} shadow-lg transition-transform hover:scale-105`}
                            >
                                <FaFileAlt className="mr-2" />
                                {t('resume.openPDF')}
                            </a>
                        </div>
                    </div>
                </div>
            </motion.div>

            <div className="mt-6 text-center">
                <p className={`text-sm ${textColor}`}>
                    {t('resume.updatedRegularly')}
                </p>
            </div>

            <div className="mt-8">
                <h3 className={`text-xl font-semibold mb-4 ${headingColor}`}>
                    {t('resume.qualifications')}
                </h3>

                <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
                    {Array.isArray(qualifications) &&
                        qualifications.map((qualification, index) => (
                            <li key={index}>{qualification}</li>
                        ))}
                </ul>

                <p className={`mt-4 ${textColor}`}>
                    {t('resume.moreDetails')}
                </p>
            </div>
        </section>
    );
};

export default Resume;
